/**
 * Mes élèves : l'onglet, la fiche d'un élève, et chaque écran que l'ancienne fiche poussait, repris par
 * un onglet de la nouvelle.
 *
 * Inscrites ici, ces routes rendent l'écran des ports pour un tuteur dont l'interrupteur est relu
 * allumé, hors live ; ailleurs leur écran legacy. Le live garde les siennes (la fiche y est un panneau,
 * « Son profil », et `student` / `messenger` y gardent l'ancien écran).
 *
 * - `student-profil` : la fiche, sur l'Aperçu ;
 * - `student`, `realizations` : la fiche, sur Activités ;
 * - `live-reports` : sur Rapports ; `school-assessments` : sur Contrôles ;
 * - `messenger` : sur Messages (Mes élèves, la notification `messages`) ;
 * - `archived-activites` : les activités archivées de l'élève (« Activités archivées » de l'onglet
 *   Activités), la page du parent sous le thème du tuteur.
 *
 * Sur les routes partagées (`student`, `realizations`, `live-reports`, `school-assessments`,
 * `messenger`), l'élève et le parent gardent les leurs : le pont du tuteur ne bascule que le tuteur.
 *
 * Chaque écran n'est chargé qu'à son premier rendu (`loadedOnRender`).
 */
import { loadedOnRender } from "../../student/loadedOnRender"
import { type TutorSpaceRouteTable, type TutorSpaceTabTable } from "../tutorSpaceRouteTypes"

type ProfileScreens = typeof import("./TutorStudentProfileScreen").TUTOR_STUDENT_PROFILE_SCREENS

/* eslint-disable @typescript-eslint/no-require-imports */
const profileOn = (route: keyof ProfileScreens) =>
    loadedOnRender(
        () => (require("./TutorStudentProfileScreen").TUTOR_STUDENT_PROFILE_SCREENS as ProfileScreens)[route],
        `TutorStudentProfileScreen(${route})`
    )

export const studentsRoutes: TutorSpaceRouteTable<"students"> = {
    "student-profil": profileOn("student-profil"),
    student: profileOn("student"),
    realizations: profileOn("realizations"),
    "live-reports": profileOn("live-reports"),
    "school-assessments": profileOn("school-assessments"),
    messenger: profileOn("messenger"),
    "archived-activites": loadedOnRender(
        () => require("./TutorArchivedActivitiesScreen").TutorArchivedActivitiesScreen,
        "TutorArchivedActivitiesScreen"
    ),
}

/**
 * `student` poussée par la relecture d'activités (`reviewer/ActivityReviewScreen.js`, `anonymous: true`) :
 * l'élève d'un autre tuteur, vu sans son identité (`StudentTabsNavigator` tait l'en-tête). La fiche du
 * design system le nommerait, avec ses coordonnées et ses gestes : cette ouverture garde l'ancien écran.
 * La garde lit `anonymous` comme l'ancien écran (`StudentTabsNavigator` : toute valeur vraie, `1`, `"1"`,
 * `"false"` compris) : un doute garde l'écran anonyme.
 */
export const studentsRouteParamGuards: Partial<Record<"student", (params: unknown) => boolean>> = {
    student: (params) => {
        const anonymous = (params as { anonymous?: unknown } | undefined)?.anonymous
        return !anonymous
    },
}

export const studentsTabs: TutorSpaceTabTable<"students"> = {
    students: loadedOnRender(() => require("./TutorStudentsScreen").TutorStudentsScreen, "TutorStudentsScreen"),
}
/* eslint-enable @typescript-eslint/no-require-imports */
